import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type { CustomBriefPreferences, CustomBriefQuestion } from '../../../types/siteRag';
import type { SavedCustomBrief } from '../../../types/site';
import { Button } from '../../ui/button';
import { openConfirmDialog } from '../../ui/confirmDialog';
import { toast } from 'sonner';
import { validateCustomBriefPreferences } from '../../../lib/reportPreferences/validation';
// Pure validator lives in lib so server code can use it without this form.
export { validateCustomBriefPreferences };

interface Props {
  value: CustomBriefPreferences;
  onChange: (next: CustomBriefPreferences) => void;
  siteId?: string;
  savedBriefs?: SavedCustomBrief[];
  onSaveToSite?: (preferences: CustomBriefPreferences, existingId?: string) => Promise<string>;
  onDeleteSaved?: (briefId: string) => Promise<void>;
}

function newQuestionId(existing: CustomBriefQuestion[]): string {
  const used = new Set(existing.map(q => q.id));
  let i = existing.length;
  while (used.has(`q${i}`)) i += 1;
  return `q${i}`;
}

export const CustomBriefPreferencesForm: React.FC<Props> = ({
  value,
  onChange,
  siteId,
  savedBriefs = [],
  onSaveToSite,
  onDeleteSaved,
}) => {
  const [loadedBriefId, setLoadedBriefId] = useState<string | 'preset-gas-btm' | ''>('');
  const [saving, setSaving] = useState(false);

  const v: CustomBriefPreferences = {
    briefName: value?.briefName ?? '',
    objective: value?.objective ?? '',
    questions: value?.questions ?? [{ id: 'q0', text: '' }],
    contextNotes: value?.contextNotes ?? '',
    counterparties: value?.counterparties ?? '',
    keyDates: value?.keyDates ?? '',
  };

  const patch = (partial: Partial<CustomBriefPreferences>) => onChange({ ...v, ...partial });

  const updateQuestion = (index: number, text: string) => {
    const next = [...v.questions];
    next[index] = { ...next[index]!, text };
    patch({ questions: next });
  };

  const moveQuestion = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= v.questions.length) return;
    const next = [...v.questions];
    const tmp = next[index]!;
    next[index] = next[target]!;
    next[target] = tmp;
    patch({ questions: next });
  };

  const removeQuestion = (index: number) => {
    if (v.questions.length <= 1) return;
    patch({ questions: v.questions.filter((_, i) => i !== index) });
  };

  const addQuestion = () => {
    patch({ questions: [...v.questions, { id: newQuestionId(v.questions), text: '' }] });
  };

  const loadPreset = (prefs: CustomBriefPreferences, sourceId: string) => {
    onChange({
      ...prefs,
      questions: prefs.questions.map(q => ({ ...q })),
    });
    setLoadedBriefId(sourceId);
  };

  const handleSaveToSite = async () => {
    const validation = validateCustomBriefPreferences(v);
    if (!validation.valid || !onSaveToSite || !siteId) {
      toast.error('Enter a brief name and at least one question before saving.');
      return;
    }
    setSaving(true);
    try {
      const existingId = loadedBriefId && loadedBriefId !== 'preset-gas-btm' ? loadedBriefId : undefined;
      const id = await onSaveToSite(v, existingId);
      setLoadedBriefId(id);
      toast.success(existingId ? 'Brief updated on site.' : 'Brief saved to site.');
    } catch (e) {
      toast.error(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSaved = async (briefId: string, name: string) => {
    if (!onDeleteSaved) return;
    const ok = await openConfirmDialog({
      title: 'Delete saved brief?',
      body: `Remove "${name}" from this site. This cannot be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await onDeleteSaved(briefId);
      if (loadedBriefId === briefId) setLoadedBriefId('');
      toast.success('Saved brief removed.');
    } catch (e) {
      toast.error(`Delete failed: ${(e as Error).message}`);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-text-muted">
          Define your diligence brief — the model will answer each question with findings, evidence, red flags, and confidence.
        </p>
        <div className="flex flex-wrap gap-2">
          {siteId && onSaveToSite && (
            <Button type="button" variant="outline" size="sm" onClick={handleSaveToSite} disabled={saving}>
              {saving ? 'Saving…' : loadedBriefId && loadedBriefId !== 'preset-gas-btm' ? 'Update on site' : 'Save to site'}
            </Button>
          )}
        </div>
      </div>

      {(savedBriefs.length > 0 || siteId) && (
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">Load saved brief</span>
          {savedBriefs.length === 0 ? (
            <p className="text-xs text-text-muted">No saved briefs yet — use Save to site after defining questions.</p>
          ) : (
            <ul className="space-y-1">
              {savedBriefs.map(b => (
                <li key={b.id} className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className="text-left text-sm text-primary hover:underline flex-1 truncate"
                    onClick={() => loadPreset(b.preferences, b.id)}
                  >
                    {b.name}
                  </button>
                  {onDeleteSaved && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive shrink-0"
                      aria-label={`Delete ${b.name}`}
                      onClick={() => void handleDeleteSaved(b.id, b.name)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <label className="block space-y-1">
        <span className="text-sm font-medium">Brief name</span>
        <input
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={v.briefName}
          onChange={e => patch({ briefName: e.target.value })}
          placeholder="e.g. Gas BTM Diligence"
        />
      </label>

      <label className="block space-y-1">
        <span className="text-sm font-medium">Objective</span>
        <textarea
          className="min-h-[72px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={v.objective ?? ''}
          onChange={e => patch({ objective: e.target.value })}
          placeholder="1-2 sentences on what this brief must decide"
        />
      </label>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Questions</span>
          <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
            <Plus className="mr-1 h-4 w-4" /> Add question
          </Button>
        </div>
        <ol className="space-y-3">
          {v.questions.map((q, index) => (
            <li key={q.id} className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-start gap-2">
                <span className="mt-2 text-xs font-semibold text-text-muted w-5">{index + 1}.</span>
                <textarea
                  className="min-h-[56px] flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={q.text}
                  onChange={e => updateQuestion(index, e.target.value)}
                  placeholder="Enter a diligence question"
                />
                <div className="flex flex-col gap-1">
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveQuestion(index, -1)} disabled={index === 0} aria-label="Move up">
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveQuestion(index, 1)} disabled={index === v.questions.length - 1} aria-label="Move down">
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeQuestion(index)} disabled={v.questions.length <= 1} aria-label="Remove">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <label className="block space-y-1">
        <span className="text-sm font-medium">Context notes (optional)</span>
        <textarea
          className="min-h-[56px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={v.contextNotes ?? ''}
          onChange={e => patch({ contextNotes: e.target.value })}
          placeholder="Deal context, assumptions, or scope boundaries"
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block space-y-1">
          <span className="text-sm font-medium">Counterparties (optional)</span>
          <input
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={v.counterparties ?? ''}
            onChange={e => patch({ counterparties: e.target.value })}
            placeholder="e.g. Constellation, Entergy"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium">Key dates (optional)</span>
          <input
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={v.keyDates ?? ''}
            onChange={e => patch({ keyDates: e.target.value })}
            placeholder="e.g. Phase 1 COD mid-2028"
          />
        </label>
      </div>
    </div>
  );
};
